import type {NegotiationSnapshot} from '@/types/negotiationSnapshot';

export interface AudienceTopCountry {
  country: string;
  percentage: number;
}

export const PAID_GIG_CREATOR_STATUS = {
  Applications: 'applications',
  Hired: 'hired',
  Declined: 'declined',
} as const;

export type PaidGigCreatorStatus =
  (typeof PAID_GIG_CREATOR_STATUS)[keyof typeof PAID_GIG_CREATOR_STATUS];

export const CREATOR_STATUS = {
  ...PAID_GIG_CREATOR_STATUS,
  Invited: 'invited',
} as const;

export type CreatorStatus =
  (typeof CREATOR_STATUS)[keyof typeof CREATOR_STATUS];

export function isPaidGigCreatorStatus(
  status: CreatorStatus
): status is PaidGigCreatorStatus {
  return status !== CREATOR_STATUS.Invited;
}

export const SAFE_COLLAB_STATUS = {
  Vetted: 'vetted',
  NotVetted: 'not_vetted',
  Warning: 'warning',
} as const;

export type SafeCollabStatus =
  (typeof SAFE_COLLAB_STATUS)[keyof typeof SAFE_COLLAB_STATUS];

export type CampaignDeliverables = 'shipments' | 'gift_codes';

export type DeliverableState =
  | 'pending'
  | 'rejected'
  | 'accepted'
  | 'posted'
  | 'processing'
  | 'not_submitted';

export type PostedState = 'ready_to_post' | 'posted';

export interface CreatorDeliverable {
  kind: string;
  state: DeliverableState;
  round: number | null;
  dueOn: string | null;
  latestFeedbackAt: string | null;
  frameCount?: number | null;
}

export type FulfillmentState =
  | 'awaiting_fulfillment'
  | 'en_route'
  | 'fulfilled';

export interface CreatorFulfillmentFields {
  deliverableId: string | null;
  fulfillmentState: FulfillmentState | null;
  deliveredAt: string | null;
  trackingCode: string | null;
  trackingCodeUrl: string | null;
  trackingCodeInvalidatedAt: string | null;
  trackingCodeInvalidatedReason: string | null;
  couponCode: string | null;
  carrier: string | null;
  deliveryStatus: string | null;
  expectedDeliveryDate: string | null;
  lastCheckpointCity: string | null;
  lastCheckpointState: string | null;
  lastCheckpointZip: string | null;
  lastCheckpointMessage: string | null;
  lastCheckpointDate: string | null;
  totalCheckpointsCount: number | null;
}

export const EMPTY_CREATOR_FULFILLMENT_FIELDS: CreatorFulfillmentFields = {
  deliverableId: null,
  fulfillmentState: null,
  deliveredAt: null,
  trackingCode: null,
  trackingCodeUrl: null,
  trackingCodeInvalidatedAt: null,
  trackingCodeInvalidatedReason: null,
  couponCode: null,
  carrier: null,
  deliveryStatus: null,
  expectedDeliveryDate: null,
  lastCheckpointCity: null,
  lastCheckpointState: null,
  lastCheckpointZip: null,
  lastCheckpointMessage: null,
  lastCheckpointDate: null,
  totalCheckpointsCount: null,
};

export const EMPTY_CREATOR_ASSET_FIELDS = {
  pendingAssetsCount: null,
  editsRequestedAssetsCount: null,
  notSubmittedCount: null,
  extraAssetsCount: null,
  postedState: null,
  isOverdue: false,
  dueSoon: false,
  maxRevisionRound: 0,
  deliverables: [],
  minimumChildContentSubmissionCount: null,
} satisfies Pick<
  CampaignCreatorRow,
  | 'pendingAssetsCount'
  | 'editsRequestedAssetsCount'
  | 'notSubmittedCount'
  | 'extraAssetsCount'
  | 'postedState'
  | 'isOverdue'
  | 'dueSoon'
  | 'maxRevisionRound'
  | 'deliverables'
  | 'minimumChildContentSubmissionCount'
>;

export const EMPTY_APPLICATION_EXPIRATION_FIELDS = {
  expiresAt: null,
  expirationHasBeenExtended: false,
} satisfies Pick<CampaignCreatorRow, 'expiresAt' | 'expirationHasBeenExtended'>;

export interface CampaignCreatorRow extends CreatorFulfillmentFields {
  id: string;
  creatorName: string;
  handle: string | null;
  avatarUrl: string | null;
  age: number | null;
  location: string | null;
  portfolioImages: string[];
  followers: number;
  engagementRate: number;
  lists: {name: string; color?: string}[];
  safeCollabStatus: SafeCollabStatus;
  campaignHistoryCount: number;
  campaignHistoryRating: number | null;
  appliedDate: string;
  profileId: string;
  aasmState: string;
  applicantStatus: CreatorStatus;
  paidGigId: string | null;
  vettingId: string | null;
  merchantConversationId: string | null;
  merchantUnreadMessagesCount: number;
  merchantCampaignUnreadMessagesCount: number;
  brandSafetyVettingDecision: string | null;
  costInCents: number | null;
  brandNegotiationSnapshot: NegotiationSnapshot | null;
  audienceTopCountry: AudienceTopCountry | null;
  averageViews: number | null;
  // Hired-only fields
  contentDue: string | null;
  // All asset counts are capped per kind by the serializer, so a surplus in one
  // kind can't fill another's slot. `extraAssetsCount` is approved output beyond
  // the requirement (over-delivery), shown as a "+N" alongside the count.
  acceptedAssetsCount: number | null;
  requiredAssetsCount: number | null;
  pendingAssetsCount: number | null;
  editsRequestedAssetsCount: number | null;
  notSubmittedCount: number | null;
  extraAssetsCount: number | null;
  // True once all required deliverables are accepted (green checkmark).
  requirementsMet: boolean;
  // True for completed/awaiting_payment gigs (also greens the assets count).
  gigInTerminalState: boolean;
  postedState: PostedState | null;
  isOverdue: boolean;
  dueSoon: boolean;
  maxRevisionRound: number;
  deliverables: CreatorDeliverable[];
  minimumChildContentSubmissionCount: number | null;
  shippingAddressFormatted: string | null;
  shippingStatus: string | null;
  brandNote: string | null;
  isShortlisted: boolean;
  // Applications-only: proposal TTL. Null/false on hired and declined.
  expiresAt: string | null;
  expirationHasBeenExtended: boolean;
}

export interface InvitedCreatorRow {
  id: string;
  profileId: string;
  creatorName: string;
  handle: string | null;
  avatarUrl: string | null;
  email: string | null;
  invitedAt: string | null;
  invitedStatus: string | null;
  origination: string | null;
  invitedAppliedAt: string | null;
  paidGigId: string | null;
}

/**
 * Raw shape returned by the V2 creators API after JSONAPI deserialization.
 */
export interface CampaignCreatorApiResponse {
  id: string;
  profileId: string;
  creatorName: string;
  handle?: string | null;
  avatarUrl: string | null;
  age: number | null;
  location: string | null;
  portfolioImages: string[] | null;
  cachedFollowerCount: number | null;
  averageEngagementRate: number | null;
  membershipLabels: {name: string}[];
  brandSafetyVettingDecision: string | null;
  completedPaidGigsCount: number;
  rating: number | null;
  appliedAt: string;
  aasmState: string;
  paidGigId: string | null;
  vettingId: string | null;
  merchantConversationId: string | null;
  merchantUnreadMessagesCount: number;
  merchantCampaignUnreadMessagesCount: number;
  costInCents: number | null;
  brandNegotiationSnapshot: NegotiationSnapshot | null;
  audienceTopCountry?: AudienceTopCountry | null;
  averageViews?: number | null;
  // Hired-only fields (absent from applications/declined responses)
  contentDue?: string | null;
  acceptedAssetsCount?: number | null;
  requiredAssetsCount?: number | null;
  pendingAssetsCount?: number | null;
  editsRequestedAssetsCount?: number | null;
  notSubmittedCount?: number | null;
  extraAssetsCount?: number | null;
  requirementsMet?: boolean;
  gigInTerminalState?: boolean;
  postedState?: PostedState | null;
  isOverdue?: boolean;
  dueSoon?: boolean;
  maxRevisionRound?: number;
  deliverables?: CreatorDeliverable[];
  minimumChildContentSubmissionCount?: number | null;
  shippingAddressFormatted?: string | null;
  shippingStatus?: string | null;
  brandNote?: string | null;
  isShortlisted?: boolean;
  deliverableId?: string | null;
  fulfillmentState?: FulfillmentState | null;
  deliveredAt?: string | null;
  trackingCode?: string | null;
  trackingCodeUrl?: string | null;
  trackingCodeInvalidatedAt?: string | null;
  trackingCodeInvalidatedReason?: string | null;
  couponCode?: string | null;
  carrier?: string | null;
  deliveryStatus?: string | null;
  expectedDeliveryDate?: string | null;
  lastCheckpointCity?: string | null;
  lastCheckpointState?: string | null;
  lastCheckpointZip?: string | null;
  lastCheckpointMessage?: string | null;
  lastCheckpointDate?: string | null;
  totalCheckpointsCount?: number | null;
  expiresAt?: string | null;
  expirationHasBeenExtended?: boolean;
}

export const VIEW_TYPE = {
  Table: 'table',
  Grid: 'grid',
} as const;

export type CreatorsViewMode = (typeof VIEW_TYPE)[keyof typeof VIEW_TYPE];

export interface CampaignCreatorCounts {
  applications: number;
  hired: number;
  declined: number;
  invited: number;
  unreadApplications: boolean;
  unreadHired: boolean;
  unreadDeclined: boolean;
}

export type PageSelectionState = 'none' | 'partial' | 'all';
